/* eslint-disable import/first */
import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Container, Paper, Divider, Grid, Avatar, Chip, IconButton, Toolbar, useTheme, useMediaQuery, alpha, Tooltip } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import goldColors from '../themes/goldColors';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AddIcon from '@mui/icons-material/Add';
import LaunchIcon from '@mui/icons-material/Launch';
import BarChartIcon from '@mui/icons-material/BarChart';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import MonitorIcon from '@mui/icons-material/Monitor';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { logout } from '../slices/authSlice';
// Ya no se usa WelcomePageHeader: esta pagina ahora vive dentro de
// NotionLayout (ruta /welcome = "Inicio" en el sidebar), que ya trae su
// propia barra superior. Un segundo AppBar aqui duplicaria el header.
// Sidebar eliminado

// Estilo del HeroSection, similar al de la página de login
const HeroSection = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.common.white,
  padding: theme.spacing(3, 2), // Reducido de 6 a 3 para optimizar espacio vertical
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url(/images/pattern.svg) repeat',
    opacity: 0.1,
    zIndex: 1,
  }
}));

// Asistente "llévame de la mano" para clientes nuevos (31 jul 2026):
// reemplaza a FinancialOnboarding.js + AddBankAccountForm embebido, que
// tenían varios bugs reales en producción (URL de desarrollo quemada en
// "Cancelar", diálogos anidados, progreso guardado en un estado propio
// desconectado del OnboardingState real). Ver el comentario al inicio de
// OnboardingWizard.js para el detalle completo.
import OnboardingWizard from './OnboardingWizard';
// Importamos el servicio de onboarding para verificar si hay registros financieros
import onboardingService from '../services/onboardingService';
// 10 ago 2026 (pedido de Augusto): aviso de prueba gratuita + precio +
// opción de pagar ya. Vivía acá, montado solo en /welcome -- pero desde
// que el registro nuevo pasa por /onboarding/intro -> /profile ->
// /accounts -> /debts (ver RegisterFormSinglePage.js), un cliente nuevo
// podía terminar todo el onboarding SIN pasar nunca por /welcome, y
// nunca veía este aviso. Se movió a NotionLayout.js (envuelve TODAS las
// rutas del estilo Notion, no solo /welcome) para que aparezca sin
// importar por cuál pantalla entra el cliente primero.

const WelcomePage = ({ firstName, section }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, language } = useLanguage();

  // Get user profile from Redux state
  const user = useSelector(state => state.auth.user);

  // Estado para controlar la apertura del asistente
  const [openFinancialOnboarding, setOpenFinancialOnboarding] = useState(false);

  // Función para manejar el cierre de sesión
  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  // Get location to read state
  const location = useLocation();
  
  // State to track which step is active and which steps are completed
  const [activeWorkflowStep, setActiveWorkflowStep] = useState('record');
  const [completedSteps, setCompletedSteps] = useState([]);
  // Estado para controlar si el usuario ya tiene registros financieros
  const [hasFinancialRecords, setHasFinancialRecords] = useState(false);
  // FIX: esta pantalla marcaba "Registrar" como "ESTÁS AQUÍ" siempre, en
  // cada login, sin importar el progreso real del usuario — activeWorkflowStep
  // arrancaba en 'record' por defecto y nada lo actualizaba con datos
  // reales del backend (a diferencia del checklist de la barra lateral,
  // que sí lee OnboardingState). Ahora se consulta el estado real del
  // onboarding para reflejar el progreso correcto en el stepper.
  //
  // 28 ago 2026 (pedido de Augusto, revierte la decision del 31 jul de
  // abajo): una vez que ya se cargaron cuentas y transacciones, esta
  // pantalla de onboarding ("Inicio" en el sidebar) ya no debe volver a
  // aparecer -- se redirige directo a /dashboard. Antes se quedaba como
  // landing permanente mostrando el stepper con todo en verde, que era
  // un paso extra innecesario para un cliente que ya está usando la app
  // a diario.
  const [checkingOnboarding, setCheckingOnboarding] = useState(true);

  // 30 ago 2026 (Augusto: "por qué aparece el onboarding, en vez del
  // tablero si ya hay transacciones metidas en el sistema?"): el redirect
  // automático de arriba (28 ago) solo confiaba en accounts_completed Y
  // debts_completed -- dos flags que el backend únicamente marca cuando el
  // cliente completa el asistente Perfil->Cuentas->Deudas paso a paso. Un
  // cliente que cargó sus datos importando un CSV/estado de cuenta (como
  // Augusto) nunca pasa por ese asistente, así que esos flags se quedan en
  // false para siempre aunque ya tenga cuentas, deudas y cientos de
  // transacciones reales -- se queda viendo "Registrar" en cada login. Se
  // fusiona acá la consulta que antes vivía en un useEffect aparte
  // (hasFinancialRecords, que sí mira si hay accounts/debts/incomes/
  // expenses reales en la base -- ver users.py::has_financial_records) para
  // que CUALQUIERA de las dos señales redirija directo al Tablero.
  useEffect(() => {
    let cancelled = false;
    const checkOnboardingState = async () => {
      try {
        const [data, hasRecords] = await Promise.all([
          onboardingService.getOnboardingData(),
          user?.id ? onboardingService.hasFinancialRecords(user.id) : Promise.resolve(false),
        ]);
        if (cancelled) return;
        const done = [];
        if (data?.accounts_completed && data?.debts_completed) done.push('record');
        if (done.includes('record') || hasRecords) {
          if (!cancelled) navigate('/dashboard', { replace: true });
          return;
        }
        setHasFinancialRecords(hasRecords);
        setCompletedSteps(done);
        setActiveWorkflowStep('record');
      } catch (error) {
        console.error('Error al verificar el estado de onboarding:', error);
      } finally {
        if (!cancelled) setCheckingOnboarding(false);
      }
    };
    checkOnboardingState();
    return () => { cancelled = true; };
  }, [navigate, user]);

  // Check if we are directed here from completing onboarding with an active step specified
  useEffect(() => {
    // First check localStorage for welcomeState
    const storedWelcomeState = localStorage.getItem('welcomeState');
    if (storedWelcomeState) {
      try {
        const parsedState = JSON.parse(storedWelcomeState);
        if (parsedState.activeStep === 'analyze') {
          // Set active step to analyze
          setActiveWorkflowStep('analyze');
          
          // Mark the record step as completed
          setCompletedSteps(prev => [...prev, 'record']);
          
          // Clear localStorage after processing to prevent it from being reapplied on refresh
          localStorage.removeItem('welcomeState');
        } else if (parsedState.activeStep === 'dashboard') {
          // Set active step to dashboard
          setActiveWorkflowStep('dashboard');
          
          // Si hay pasos completados específicados, usarlos; si no, marcar los pasos 'record' y 'analyze' como completados
          if (parsedState.completed && Array.isArray(parsedState.completed)) {
            setCompletedSteps(parsedState.completed);
          } else {
            setCompletedSteps(['record', 'analyze']);
          }
          
          // Hacer scroll automáticamente a la sección de dashboard
          setTimeout(() => {
            const dashboardSection = document.getElementById('dashboard-section');
            if (dashboardSection) {
              dashboardSection.scrollIntoView({ behavior: 'smooth' });
            }
          }, 500);
          
          // Clear localStorage after processing
          localStorage.removeItem('welcomeState');
        }
      } catch (error) {
        console.error('Error parsing welcomeState:', error);
      }
    } 
    // Then check React Router state as fallback
    else if (location.state?.activeStep === 'analyze') {
      setActiveWorkflowStep('analyze');
      setCompletedSteps(prev => [...prev, 'record']);
      
      // Clear the state after processing to prevent it from being reapplied on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);
  
  // Get the auth token from the Redux store
  const authToken = useSelector((state) => state.auth.token);
  
  // Function to navigate to dashboard
  const goToDashboard = () => {
    // Set onboarding as completed to prevent any redirect loops
    localStorage.setItem('onboardingCompleted', 'true');
    
    // Ensure the token is saved in sessionStorage (3 ago 2026: no
    // localStorage -- ver services/api.js. Cerrar el navegador cierra la
    // sesión.)
    if (authToken) {
      sessionStorage.setItem('token', authToken);
      // Also set the user as authenticated
      localStorage.setItem('isAuthenticated', 'true');
    }
    
    // Transfer financial data from welcomeState to financialData for the dashboard
    const welcomeStateData = localStorage.getItem('welcomeState');
    if (welcomeStateData) {
      try {
        // Save the same data with the key the dashboard is looking for
        localStorage.setItem('financialData', welcomeStateData);
        console.log('Financial data transferred to dashboard');
      } catch (error) {
        console.error('Error transferring financial data:', error);
      }
    }
    
    // Navigate to dashboard
    navigate('/dashboard');
  };

  // Function to navigate to reports (Informes / Reporte Inteligente IA)
  const goToReports = () => {
    navigate('/reports');
  };

  // Infographic data for the workflow
  const workflowSteps = [
    { 
      key: 'record',
      icon: <AddIcon />, 
      label: language === 'es' ? t('welcome.workflowSteps.record', 'Registrar') : "Record", 
      description: language === 'es' ? t('welcome.workflowSteps.recordDesc', 'Registre sus transacciones') : "Log your transactions", 
      current: activeWorkflowStep === 'record',
      completed: completedSteps.includes('record'),
      onClick: null,
      disabled: hasFinancialRecords // Deshabilitar si ya tiene registros financieros
    },
    { 
      key: 'dashboard',
      icon: <DashboardIcon />, 
      label: language === 'es' ? 'Panel' : "Dashboard", 
      description: language === 'es' ? 'Monitoree sus cuentas' : "Monitor your accounts", 
      current: activeWorkflowStep === 'dashboard',
      completed: completedSteps.includes('dashboard'),
      onClick: goToDashboard
    },
    { 
      key: 'analyze',
      icon: <BarChartIcon />, 
      label: language === 'es' ? t('welcome.workflowSteps.analyze', 'Analizar') : "Analyze", 
      description: language === 'es' ? t('welcome.workflowSteps.analyzeDesc', 'Vea patrones de gastos') : "View spending patterns", 
      current: activeWorkflowStep === 'analyze',
      completed: completedSteps.includes('analyze'),
      onClick: goToReports
    },
  ];

  // Theme and media query for responsive design
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  
  // Handler for opening financial onboarding
  const handleOpenFinancialOnboarding = () => {
    setOpenFinancialOnboarding(true);
  };

  if (checkingOnboarding) {
    // Evita el parpadeo del asistente "Registrar" mientras se confirma
    // si el onboarding ya estaba completo (en cuyo caso se redirige a
    // /dashboard antes de llegar a este punto).
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Typography variant="body1" color="text.secondary">
          {language === 'es' ? 'Cargando...' : 'Loading...'}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Hero section con estilo de login page */}
      <HeroSection>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6} lg={5}>
              <Typography variant="h3" component="h1" gutterBottom>
                {firstName ? `${language === 'es' ? t('welcome.title', 'Bienvenido') : 'Welcome'}, ${firstName}!` : language === 'es' ? t('welcome.title', 'Bienvenido') : 'Welcome!'}
              </Typography>
              <Typography variant="h6" color="textSecondary" paragraph>
                {language === 'es' ? t('welcome.subtitle', 'Gracias por unirse a nuestra comunidad financiera. Juntos, mejoraremos su bienestar financiero.') : 'Thank you for joining our financial community. Together, we\'ll improve your financial well-being.'}
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </HeroSection>
      
      {/* Contenido principal */}
      <Box sx={{ 
        width: '100%',
        py: 1, // Reducido de 2 a 1
        bgcolor: (theme) => theme.palette.background.default
      }}>
        <Container maxWidth="lg">
          <Paper elevation={2} sx={{ p: { xs: 1, md: 1.5 }, borderRadius: 2, mb: 1 }}>
              
              {/* Infographic section */}
              <Box sx={{ mb: 1, p: { xs: 1, md: 1.5 }, bgcolor: 'background.default', borderRadius: 2 }}>
                <Typography variant="h5" sx={{ mb: 1, textAlign: 'center', fontWeight: 'bold', color: 'primary.main' }}>
                  {language === 'es' ? t('welcome.journey', 'Organicemos sus finanzas juntos, ¡es fácil!') : 'Let\'s organize your finances together, it\'s easy!'}
                </Typography>
                <Typography variant="subtitle2" sx={{ mb: 1.5, textAlign: 'center', maxWidth: '700px', mx: 'auto' }}>
                  {language === 'es' ? t('welcome.journeyDesc', 'Le guiaremos paso a paso para configurar su perfil financiero sin complicaciones') : 'We\'ll guide you step by step to set up your financial profile without complications'}
                </Typography>
            
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: { xs: 'wrap', md: 'nowrap' }, gap: 1.5 }}>
                {workflowSteps.map((step, index) => (
                  <React.Fragment key={index}>
                    {step.current ? (
                      <Box sx={{ position: 'relative' }}>
                        <Box 
                          sx={{ 
                            position: 'absolute', 
                            top: -15, 
                            left: '50%', 
                            transform: 'translateX(-50%)',
                            zIndex: 2,
                            bgcolor: 'error.main',
                            color: 'white',
                            px: 1,
                            py: 0.25,
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <LocationOnIcon fontSize="small" sx={{ mr: 0.5 }} />
                          <Typography variant="caption" fontWeight="bold">{language === 'es' ? t('welcome.youAreHere', 'ESTÁS AQUÍ') : 'YOU ARE HERE'}</Typography>
                        </Box>
                        <Paper 
                          elevation={4} 
                          sx={{ 
                            p: 1.5, 
                            minWidth: { xs: '40%', sm: '160px' }, 
                            textAlign: 'center',
                            flex: '1 1 0',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '2px solid',
                            borderColor: 'error.main',
                            position: 'relative'
                          }}
                        >
                          <Avatar 
                            sx={{ 
                              bgcolor: 'primary.main', 
                              width: 45, 
                              height: 45,
                              mb: 0.5
                            }}
                          >
                            {step.icon}
                          </Avatar>
                          <Typography variant="h6" fontWeight="bold">{step.label}</Typography>
                          <Typography variant="body2" sx={{ mb: 1 }}>{step.description}</Typography>

                          {/* El paso "actual" puede ser Registrar (primera vez) o
                              Panel (una vez que ya se cargaron cuentas/deudas y
                              esta pagina se usa como landing permanente) — cada
                              uno necesita su propia accion, no siempre "empezar
                              a anadir datos". */}
                          {step.key === 'dashboard' ? (
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              onClick={goToDashboard}
                              sx={{ mt: 0.5 }}
                            >
                              {language === 'es' ? 'Ir al Panel' : 'Go to Dashboard'}
                            </Button>
                          ) : (
                            <Button
                              variant="contained"
                              color="error"
                              size="small"
                              onClick={() => setOpenFinancialOnboarding(true)}
                              sx={{ mt: 0.5 }}
                            >
                              {language === 'es' ? t('welcome.startAddingData', 'Comenzar a Añadir Datos') : 'Start Adding Data'}
                            </Button>
                          )}
                        </Paper>
                      </Box>
                    ) : step.completed ? (
                      <Paper 
                        elevation={2} 
                        sx={{ 
                          p: 1.5, 
                          minWidth: { xs: '40%', sm: '160px' }, 
                          textAlign: 'center',
                          flex: '1 1 0',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: 0.7,
                          bgcolor: '#f5f5f5'
                        }}
                      >
                        <Avatar 
                          sx={{ 
                            bgcolor: 'grey.400', 
                            width: 45, 
                            height: 45,
                            mb: 0.5
                          }}
                        >
                          {step.icon}
                        </Avatar>
                        <Typography variant="h6" fontWeight="bold" color="text.secondary">{step.label}</Typography>
                        <Typography variant="body2" color="text.secondary">{step.description}</Typography>
                        
                        {/* Completed indicator */}
                        <Chip 
                          label={language === 'es' ? 'Completado' : 'Completed'}
                          color="success"
                          size="small"
                          sx={{ mt: 1, fontSize: '0.75rem' }}
                          icon={<CheckCircleIcon fontSize="small" />}
                        />
                      </Paper>
                    ) : (
                      <Tooltip 
                        title={step.disabled ? (language === 'es' ? 'Ya has registrado datos financieros anteriormente' : 'You have already recorded financial data previously') : ""} 
                        placement="top" 
                        arrow
                        disableHoverListener={!step.disabled}
                      >
                      <Paper 
                        elevation={2} 
                        sx={{ 
                          p: 1.5, 
                          minWidth: { xs: '40%', sm: '160px' }, 
                          textAlign: 'center',
                          flex: '1 1 0',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: step.disabled ? 'not-allowed' : (step.onClick ? 'pointer' : 'default'),
                          opacity: step.disabled ? 0.6 : 1,
                          '&:hover': {
                            bgcolor: step.onClick && !step.disabled ? 'rgba(25, 118, 210, 0.04)' : 'inherit'
                          }
                        }}
                        onClick={step.disabled ? null : (step.onClick || undefined)}
                      >
                        <Avatar 
                          sx={{ 
                            bgcolor: 'primary.main', 
                            width: 45, 
                            height: 45,
                            mb: 0.5
                          }}
                        >
                          {step.icon}
                        </Avatar>
                        <Typography variant="h6" fontWeight="bold">{step.label}</Typography>
                        <Typography variant="body2">{step.description}</Typography>
                        
                        {step.key === 'dashboard' && (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              goToDashboard();
                            }}
                            sx={{ mt: 1 }}
                          >
                            {language === 'es' ? 'Ir al Panel' : 'Go to Dashboard'}
                          </Button>
                        )}
                        {step.key === 'analyze' && (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              goToReports();
                            }}
                            sx={{ mt: 1 }}
                          >
                            {language === 'es' ? 'Ver Informes' : 'View Reports'}
                          </Button>
                        )}
                      </Paper>
                      </Tooltip>
                    )}
                    
                    {index < workflowSteps.length - 1 && (
                      <ArrowForwardIcon 
                        color="action" 
                        sx={{ 
                          fontSize: 30, 
                          display: { xs: 'none', md: 'block' }
                        }} 
                      />
                    )}
                  </React.Fragment>
                ))}
                </Box>
              </Box>
          </Paper>
        </Container>
      </Box>
        
      {/* Sección de Dashboard */}
      <Box id="dashboard-section" sx={{ py: 4, bgcolor: '#f5f8fa' }}>
        <Container maxWidth="lg">
          <Box mb={4}>
            <Typography variant="h4" gutterBottom fontWeight="bold" color="primary">
              {language === 'es' ? 'Panel de Monitoreo' : 'Dashboard'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {language === 'es' ? 
                'Visualice y gestione sus finanzas desde un solo lugar.' : 
                'Visualize and manage your finances from one place.'}
            </Typography>
          </Box>
          
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6} lg={4}>
              <Paper elevation={2} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                    <AccountBalanceIcon />
                  </Avatar>
                  <Typography variant="h6">
                    {language === 'es' ? 'Cuentas Bancarias' : 'Bank Accounts'}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {language === 'es' ? 
                    'Acceda a todas sus cuentas bancarias y vea sus balances.' : 
                    'Access all your bank accounts and view your balances.'}
                </Typography>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={goToDashboard}
                  sx={{ mt: 'auto' }}
                >
                  {language === 'es' ? 'Ver Cuentas' : 'View Accounts'}
                </Button>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6} lg={4}>
              <Paper elevation={2} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                    <AttachMoneyIcon />
                  </Avatar>
                  <Typography variant="h6">
                    {language === 'es' ? 'Ingresos y Gastos' : 'Income & Expenses'}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {language === 'es' ? 
                    'Analice sus fuentes de ingresos y patrones de gastos.' : 
                    'Analyze your income sources and expense patterns.'}
                </Typography>
                <Button 
                  variant="contained" 
                  color="success"
                  onClick={goToDashboard}
                  sx={{ mt: 'auto' }}
                >
                  {language === 'es' ? 'Ver Transacciones' : 'View Transactions'}
                </Button>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6} lg={4}>
              <Paper elevation={2} sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                    <BarChartIcon />
                  </Avatar>
                  <Typography variant="h6">
                    {language === 'es' ? 'Resumen Financiero' : 'Financial Summary'}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {language === 'es' ? 
                    'Visualice un resumen de su situación financiera actual.' : 
                    'View a summary of your current financial situation.'}
                </Typography>
                <Button 
                  variant="contained" 
                  color="info"
                  onClick={goToDashboard}
                  sx={{ mt: 'auto' }}
                >
                  {language === 'es' ? 'Ver Resumen' : 'View Summary'}
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      {/* Banner de cierre al estilo login page */}
      <HeroSection>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, py: 1 }}>
          <Grid container spacing={2} alignItems="center" justifyContent="space-between">
            <Grid item xs={12} md={8}>
              <Typography variant="h5" gutterBottom>
                {language === 'es' ? t('welcome.cta.title', 'Gestione sus finanzas con facilidad') : 'Manage your finances with ease'}
              </Typography>
              <Typography variant="body1">
                {language === 'es' ? t('welcome.cta.description', 'Todas sus transacciones, cuentas y análisis en un solo lugar.') : 'All your transactions, accounts and analysis in one place.'}
              </Typography>
            </Grid>
            {/* Botón de Comenzar Ahora eliminado */}
          </Grid>
        </Container>
      </HeroSection>
      
      {/* Asistente de onboarding (Perfil → Cuentas → Deudas) */}
      <OnboardingWizard
        open={openFinancialOnboarding}
        onClose={() => {
          setOpenFinancialOnboarding(false);
          // Al cerrar (incluyendo cuando el asistente termina y navega solo
          // al Tablero), refrescamos el estado del stepper de esta página
          // por si el cliente vuelve a "Inicio" más tarde.
          setCompletedSteps(prev => (prev.includes('record') ? prev : [...prev, 'record']));
          setActiveWorkflowStep('analyze');
        }}
      />
    </>
  );
};

export default WelcomePage;
